"use client";

import { useState, useRef, useEffect, useTransition } from "react";
import { useLocale, useTranslations } from "next-intl";
import { Globe, Check, ChevronDown } from "lucide-react";
import { useRouter, usePathname } from "@/i18n/navigation";
import { routing } from "@/i18n/routing";
import { useLocalizedPaths } from "./LocalizedPathsContext";

const LOCALE_FLAGS: Record<string, string> = {
      tr: "🇹🇷",
      en: "🇬🇧",
      de: "🇩🇪",
      ru: "🇷🇺",
};

export default function LanguageSwitcher() {
      const [open, setOpen] = useState(false);
      const [isPending, startTransition] = useTransition();
      const currentLocale = useLocale();
      const t = useTranslations("language");
      const router = useRouter();
      const pathname = usePathname();
      const localizedPaths = useLocalizedPaths();
      const ref = useRef<HTMLDivElement>(null);

      useEffect(() => {
            const handleClickOutside = (e: MouseEvent) => {
                  if (ref.current && !ref.current.contains(e.target as Node)) {
                        setOpen(false);
                  }
            };
            document.addEventListener("mousedown", handleClickOutside);
            return () => document.removeEventListener("mousedown", handleClickOutside);
      }, []);

      const handleChange = (nextLocale: string) => {
            setOpen(false);
            const overridePath = localizedPaths?.[nextLocale];
            startTransition(() => {
                  if (overridePath) {
                        window.location.href = overridePath;
                        return;
                  }
                  router.replace(pathname as any, { locale: nextLocale as any });
            });
      };

      return (
            <div ref={ref} className="relative">
                  <button
                        onClick={() => setOpen((prev) => !prev)}
                        disabled={isPending}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium text-gray-600 hover:text-[#A68F72] hover:bg-[#A68F72]/5 transition-all duration-200 disabled:opacity-60"
                        aria-label={t("label")}
                  >
                        <Globe size={16} />
                        <span className="text-base leading-none">{LOCALE_FLAGS[currentLocale]}</span>
                        <span className="uppercase text-xs font-bold">{currentLocale}</span>
                        <ChevronDown size={14} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
                  </button>

                  {open && (
                        <div className="absolute right-0 top-full mt-2 w-44 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                              <div className="p-1.5">
                                    {routing.locales.map((loc) => {
                                          const active = loc === currentLocale;
                                          return (
                                                <button
                                                      key={loc}
                                                      onClick={() => handleChange(loc)}
                                                      className={`
                                                            w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all
                                                            ${active
                                                                  ? "bg-[#A68F72]/10 text-[#A68F72] font-semibold"
                                                                  : "text-gray-600 hover:bg-gray-50"
                                                            }
                                                      `}
                                                >
                                                      <span className="text-lg leading-none">{LOCALE_FLAGS[loc]}</span>
                                                      <span className="grow text-left">{t(loc)}</span>
                                                      {active && <Check size={14} />}
                                                </button>
                                          );
                                    })}
                              </div>
                        </div>
                  )}
            </div>
      );
}
